import { notEmpty } from "@product/scmp-sdk";
import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { ClientSideSuspense } from "scmp-app/components/common/client-side-suspense";
import { DefaultPageSize } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { SearchResultList } from "./search-result-list";
import { StyledLoading } from "./styles";

export const query = graphql`
  query searchResultPanelQuery(
    $query: String!
    $sectionIds: [String!]
    $paywallTypeIds: [String!]
    $excludePaywallTypeIds: [String!]
    $orderBy: ArticlesSearchOrderByInput
    $first: Int = 10
    $after: String
    $startDate: Timestamp
    $endDate: Timestamp
  ) {
    ...searchResultListArticleSearchQuery
    topicSearch(first: 10, filter: { query: $query }) {
      ...searchResultListTopicSearchConnection
    }
    sectionSearch(first: 10, filter: { query: $query }) {
      ...searchResultListSectionSearchConnection
    }
  }
`;

export const SearchResultPanel: FunctionComponent = () => {
  const { events: routerEvents, query: q } = useRouter();
  const { currentValue, dateRange, excludePaywallTypeIds, orderBy, paywallTypeIds, sectionIds } =
    useSearchContext();
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<searchResultPanelQuery>(query);

  useEffect(() => {
    const handler = () => {
      if (!notEmpty(currentValue)) return;
      if (q.value === currentValue) return;

      loadQuery(
        {
          excludePaywallTypeIds,
          first: DefaultPageSize,
          orderBy,
          paywallTypeIds,
          query: currentValue,
          sectionIds,
          ...dateRange,
        },
        { fetchPolicy: "network-only" },
      );
    };
    routerEvents.on("routeChangeComplete", handler);
    return () => {
      routerEvents.off("routeChangeComplete", handler);
      disposeQuery();
    };
  }, [
    currentValue,
    dateRange,
    disposeQuery,
    excludePaywallTypeIds,
    loadQuery,
    orderBy,
    paywallTypeIds,
    q.value,
    routerEvents,
    sectionIds,
  ]);

  useEffect(() => {
    if (!notEmpty(currentValue)) return;
    if (q.value !== currentValue) return;
    loadQuery(
      {
        excludePaywallTypeIds,
        first: DefaultPageSize,
        orderBy,
        paywallTypeIds,
        query: currentValue,
        sectionIds,
        ...dateRange,
      },
      { fetchPolicy: "network-only" },
    );
    return () => {
      disposeQuery();
    };
  }, [
    currentValue,
    dateRange,
    disposeQuery,
    excludePaywallTypeIds,
    loadQuery,
    orderBy,
    paywallTypeIds,
    q.value,
    sectionIds,
  ]);

  if (!notEmpty(currentValue)) return null;

  return (
    <ClientSideSuspense fallback={<StyledLoading color="gray" />}>
      {queryReference && <SearchResultList queryReference={queryReference} />}
    </ClientSideSuspense>
  );
};

SearchResultPanel.displayName = "SearchResultPanel";
